import { useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { uptimeKumaService, type SystemStatus, type Monitor } from '@/lib/services/uptimeKuma'

export function useSystemStatus() {
  return useQuery({
    queryKey: ['system-status'],
    queryFn: () => uptimeKumaService.getSystemStatus(),
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 15000, // Consider data stale after 15 seconds
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

export function useMonitorDetails(monitorId: number | undefined) {
  return useQuery({
    queryKey: ['monitor-details', monitorId],
    queryFn: () => monitorId ? uptimeKumaService.getMonitorDetails(monitorId) : null,
    enabled: !!monitorId,
    refetchInterval: 60000, // Refetch every minute for details
    staleTime: 30000,
  })
}

// Custom hook for animated counters
export function useAnimatedCounter(
  target: number,
  duration: number = 2000,
  enabled: boolean = true
) {
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    if (!enabled) return

    let startTime: number
    let animationFrame: number

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime
      const progress = Math.min((currentTime - startTime) / duration, 1)
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      const value = target * easeOutQuart
      
      setCurrent(value)

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate)
      }
    }

    animationFrame = requestAnimationFrame(animate)

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
      }
    }
  }, [target, duration, enabled])

  return current
}